import type { ReactNode } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const EFFECTIVE = '23 May 2026';

const SECTIONS: { id: string; title: string; body: ReactNode }[] = [
  {
    id: 'acceptance',
    title: 'Acceptance of terms',
    body: (
      <p>
        By using Alhudah you agree to these terms and to our Privacy Policy. If
        you do not agree with them, please do not use the app.
      </p>
    ),
  },
  {
    id: 'what-it-is',
    title: 'What Alhudah is',
    body: (
      <p>
        Alhudah is a free tool for reading, understanding, searching, and
        memorizing the Quran. It is a reading and study companion — it is not a
        source of religious rulings, and it does not replace learning from
        qualified teachers.
      </p>
    ),
  },
  {
    id: 'the-text',
    title: 'The Quranic text',
    body: (
      <p>
        The Arabic Quranic text is served verbatim from the source database and
        is never altered, paraphrased, or rewritten. Translations and tafsir
        are the work of their respective translators and scholars, and are
        provided to aid understanding.
      </p>
    ),
  },
  {
    id: 'ai-answers',
    title: 'AI-assisted answers',
    body: (
      <p>
        The Ask feature produces AI-generated answers grounded in the Quran and
        tafsir. These answers are an aid to study and may be incomplete or
        imperfect. They are not a fatwa or a religious ruling. For any matter
        that requires a ruling, consult a qualified scholar.
      </p>
    ),
  },
  {
    id: 'your-account',
    title: 'Your account',
    body: (
      <p>
        If you create an account, you are responsible for the activity that
        happens under it. The settings, bookmarks, and progress you save belong
        to you, and you may delete them at any time.
      </p>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable use',
    body: (
      <p>
        Please use Alhudah respectfully. Do not attempt to disrupt, overload,
        reverse-engineer, or abuse the service or its AI features, and do not
        use it to misrepresent the text or meaning of the Quran to others.
      </p>
    ),
  },
  {
    id: 'availability',
    title: 'Availability and disclaimer',
    body: (
      <>
        <p>
          Alhudah is provided “as is”. We work to keep it accurate, secure, and
          available, but we cannot guarantee uninterrupted or error-free
          service.
        </p>
        <p>
          To the fullest extent permitted by law, Alhudah is not liable for
          indirect or consequential loss arising from use of the app.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    body: (
      <p>
        We may update these terms as the app develops. When we do, we will
        revise the effective date above. Continuing to use Alhudah after a
        change means you accept the updated terms.
      </p>
    ),
  },
];

/** Terms of Use — numbered sections with a sticky table of contents. */
export function Terms() {
  useDocumentTitle('Terms of Use');

  return (
    <section className="container-wide pb-24 pt-12 sm:pt-16">
      <header className="mb-10 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-beam">
          Legal
        </p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-night-50 sm:text-4xl">
          Terms of Use
        </h1>
        <p className="mt-2 text-sm text-night-300">Effective: {EFFECTIVE}</p>
        <p className="mt-5 leading-relaxed text-night-200">
          These terms set out the agreement between you and Alhudah when you use
          the app. By using Alhudah, you accept them. They are short, and
          written plainly.
        </p>
      </header>

      <div className="grid gap-10 lg:grid-cols-[200px_1fr]">
        <nav aria-label="Contents" className="hidden lg:block">
          <div className="sticky top-24">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-night-300">
              Contents
            </p>
            <ol className="mt-4 space-y-2.5">
              {SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="flex gap-2 text-sm text-night-200 transition-colors hover:text-beam-pale"
                  >
                    <span className="num-tight text-beam/70">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </nav>

        <div className="space-y-12">
          {SECTIONS.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <div className="flex items-baseline gap-3">
                <span className="num-tight font-display text-sm font-bold text-beam">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h2 className="font-display text-xl font-bold text-night-50">
                  {s.title}
                </h2>
              </div>
              <div className="mt-3 space-y-3 leading-relaxed text-night-200">
                {s.body}
              </div>
            </section>
          ))}
        </div>
      </div>
    </section>
  );
}
